import {ajax, redirectPage} from '../../common/utils.js';
import {SET_ACCOUNT_DATA, SET_ACCOUNT_MODE, SET_SHOW_ADDRESS_VALIDATION_MESSAGE} from '../actionTypes';
import {URLs, AccountModes, FetchingTypes, SubscriptionStatuses} from '../../common/constants';
import {activeOrderParser, calculateOrdersTotals, setCoupon, setStoreCreditsData} from "../store/storeActions";
import {setNextDeliveryDate, setSummary, subscriptionSummaryParser,
    setVacations, setRequiredMakeUpData, setWebOrder, setCanSaveDeliveryHold, setSubscriptionOnPermanentHold} from "../common/subscriptionSummaryActions";
import {setActiveOrder, parseCouponData} from "../common/commonActions";
import {setMessageData, messageDataParser} from "../common/messagePageActions";
import {setMemberData} from "../login/memberDetailsActions";
import {setSignupData} from "../join/joinActions";
import {buildSubscriptionFromState} from '../../common/subscription';
import {setDirty} from '../common/commonActions';
import {fetchingData, dataFetched, errorFetching} from "../common/fetchingActions";

function setAccountData(accountData) {
    return {
        type: SET_ACCOUNT_DATA,
        accountData
    };
}

export function setAccountMode(mode, params) {
    return {
        type: SET_ACCOUNT_MODE,
        mode,
        params
    }
}

export function getAccountData() {
    return function(dispatch, getState){
        const state = getState();
        const fetchUrl = '/dispatcher/WebStoreService.getAccountData';
        const data = {
            farmKey: state.staticData.farmKey,
            seasonKey: state.staticData.seasonKey,
            memberKey: state.memberDetails.key
        };
        dispatch(fetchingData(FetchingTypes.LOAD_ACCOUNT_DATA));
        return ajax(fetchUrl, data)
            .then(json => {
                if(json.error) {
                    throw json.error;
                } else {
                    return parseAccountDataResponse(json, state.staticData);
                }
            })
            .then(accountData => {
                const summary = accountData.data.subscriptionSummary;
                if(isRedirectToFutureSeason(summary, state.staticData.farmUrl)) {
                    dispatch(changeSeason(summary.seasons[0]));
                } else {
                    dispatch(setAccountData(accountData.data));
                    dispatch(setMemberData(accountData.data.member));
                    dispatch(setSummary(summary));
                    dispatch(setMessageData(accountData.messageData));
                    dispatch(setAccountMode(AccountModes.VIEW));
                    dispatch(dataFetched());
                }
            })
            .catch(error => {
                dispatch(errorFetching(error, {action:getAccountData}));
            })
    }
}

function parseAccountDataResponse(json, staticData) {
    let subscriptionSummary = subscriptionSummaryParser(json, {staticData});
    let messageData = messageDataParser(subscriptionSummary, {staticData, flowType:URLs.ACCOUNT});
    return {
        data:json,
        messageData
    };
}

export function changeSeason(season){
    return function(dispatch, getState) {
        const url = window.location.origin + "/" + URLs.ACCOUNT + "/" + getState().staticData.farmUrl + "/" + season.name;
        window.location.assign(url);
    }
}

export function openChangePickupSite(onlyOnce){
    return function (dispatch){
        dispatch(setAccountMode(AccountModes.CHANGE_PICKUP_SITE, {changePickupSiteOnlyOnce: onlyOnce}));
    }
}

export function openChangeSubscription(){
    return function (dispatch){
        dispatch(setAccountMode(AccountModes.CHANGE_SUBSCRIPTION));
    }
}

export function openAddSubscription(){
    return function (dispatch, getState){
        const subscriptionKey = getState().summary.key;
        const joinURL = redirectPage(URLs.JOIN, getState().staticData) + "?mode=add&s=" + subscriptionKey;
        window.location.assign(joinURL);
    }
}

export function getOrderData(orderKey) {
    return function(dispatch, getState){
        const fetchUrl = '/dispatcher/WebStoreService.getOrderData';
        const data = {
            orderKey: orderKey,
            memberKey: getState().memberDetails.key,
            includeTitles:true
        };
        dispatch(fetchingData(FetchingTypes.LOAD_ORDER_DATA));
        return ajax(fetchUrl, data)
            .then(json => activeOrderParser(json))
            .then(webOrder => {
                dispatch(setActiveOrder(webOrder));
                if(webOrder.usedFarmigoCredits) {
                    dispatch(setStoreCreditsData({credits:webOrder.usedFarmigoCredits}));
                }
                dispatch(setCoupon(parseCouponData(webOrder.coupon, true)));
                dispatch(calculateOrdersTotals(webOrder.items));
            }).then(()=> dispatch(dataFetched()))
            .catch(error => {
                console.log(error);
                dispatch(errorFetching(error));
            })
    }
}

export function updateVacations(vacationStart, vacationEnd, makeUpSchedules, makeUpDates) {
    return function(dispatch, getState){
        const fetchUrl = '/dispatcher/WebStoreService.updateVacation';
        let data = {
            farmKey: getState().staticData.farmKey,
            memberKey: getState().memberDetails.key,
            subscriptionKey:getState().summary.key,
            vacationStart:JSON.stringify(vacationStart),
            vacationEnd:JSON.stringify(vacationEnd)
        };
        if (makeUpSchedules && makeUpDates) {
            data.makeUpSchedules = JSON.stringify(makeUpSchedules);
            data.makeUpDates = JSON.stringify(makeUpDates);
        }
        dispatch(fetchingData(FetchingTypes.UPDATE_VACATION));
        return ajax(fetchUrl, data)
            .then(json => {
                if (json.requiredMakeUpSchedules) {
                    dispatch(setRequiredMakeUpData(json.requiredMakeUpSchedules, json.requiredMakeUpSchedulesCount, json.requiredMakeUpSchedulesDates));
                    throw({message: 'Your hold dates were not saved yet. To complete the operation please schedule your make up shares dates.'});
                }
                else if (!json.isAllowed) {
                    throw({message: json.errorMessage});
                } else {
                    if (json.reloadRequired) {
                        dispatch(getAccountData());
                        dispatch(setNextDeliveryDate(json.nextDeliveryDate));
                        dispatch(setVacations(vacationStart, vacationEnd));
                        dispatch(setRequiredMakeUpData());
                    } else {
                        dispatch(setNextDeliveryDate(json.nextDeliveryDate));
                        dispatch(setVacations(vacationStart, vacationEnd));
                        dispatch(setRequiredMakeUpData());
                    }
                }
            })
            .then(()=> dispatch(dataFetched()))
            .catch(error => {
                dispatch(errorFetching(error));
            })
    }
}

export function getPickupSiteAvailableForChange() {
    return function(dispatch, getState){
        const fetchUrl = '/dispatcher/WebStoreService.getPickupSiteAvailableForChange';
        const state =  getState();
        const sharesKeys = state.summary.shareItems.map(function (share) {
            return share.key;
        });
        const data = {
            farmKey: state.staticData.farmKey,
            memberKey: state.memberDetails.key,
            seasonKey:state.staticData.seasonKey,
            subscriptionKey:state.summary.key,
            subscriptionShares: JSON.stringify(sharesKeys)
        };
        dispatch(fetchingData(FetchingTypes.LOAD_SIGNUP_DATA));
        return ajax(fetchUrl, data)
            .then(json => dispatch(setPickupSiteForChange(json)))
            .then(()=> dispatch(dataFetched()))
            .catch(error => {
                dispatch(errorFetching(error, {action:getPickupSiteAvailableForChange}));
            })
    }
}

function setPickupSiteForChange(json) {
    return function(dispatch) {
        dispatch(setSignupData({pickupSitesData: json}));
    }
}

export function updatePickupSite(pickupSite, pickupSiteWaitList) {
    return function (dispatch, getState) {
        if(!pickupSite){
            return;
        }
        const state = getState();
        if (!isChangePickupSiteAllowed(pickupSite, state)) {
            dispatch(errorFetching({message: 'Cannot change delivery day. Please contact us in order to make the change.'}));
            return;
        }

        const fetchUrl = '/dispatcher/WebStoreService.updatePickupSite';
        const data = {
            farmKey: state.staticData.farmKey,
            memberKey: state.memberDetails.key,
            subscriptionKey: state.summary.key,
            pickupSiteKey: pickupSite?pickupSite.key:state.summary.pickupSite.key,
            pickupSiteWaitListKey: pickupSiteWaitList?pickupSiteWaitList.key:null,
            onlyOnce: (state.account.params && state.account.params.changePickupSiteOnlyOnce)
        };
        dispatch(fetchingData(FetchingTypes.UPDATE_PICKUP_SITE));
        return ajax(fetchUrl, data)
            .then(json => {
                if(json.statusCode === "OK") {
                    dispatch(setNextDeliveryDate(json.message));
                    dispatch(setAccountMode(AccountModes.VIEW));
                } else {
                    dispatch(setShowAddressValidationMessage("Failed to change pickup site."));
                }
            }).then(dispatch(setDirty(false)))
            .then(()=> dispatch(dataFetched()))
            .catch(error => {
                dispatch(setDirty(false));
                dispatch(errorFetching(error));
            })
    }
}

function isChangePickupSiteAllowed(pickupSite, state) {
    if (!pickupSite) {
        return true;
    }
    let isOnce = false;
    state.summary.shareSchedules.map(schedule => {
        if (state.summary.schedules[schedule].isOnce) {
            isOnce = true;
        }
    });
    return !(isOnce && pickupSite.deliveryDays !== state.summary.origPickupSite.deliveryDays);
}

export function changeSubscription(){
    return function (dispatch, getState){
        const state = getState();
        const fetchUrl = '/dispatcher/WebStoreService.changeSubscription';
        const subscription = buildSubscriptionFromState(state);
        const data = {
            farmKey: state.staticData.farmKey,
            memberKey: state.memberDetails.key,
            subscriptionKey: state.summary.key,
            shareItems: JSON.stringify(subscription.shareItems),
            shareItemQuantities: JSON.stringify(subscription.shareItemQuantities),
            shareItemSchedules: JSON.stringify(subscription.shareItemSchedules),
            waitListItems: JSON.stringify(subscription.waitListItems)
        };
        dispatch(fetchingData(FetchingTypes.CHANGE_SUBSCRIPTION));
        return ajax(fetchUrl, data)
            .then(() => dispatch(setAccountMode(AccountModes.CHANGE_SUBSCRIPTION_THANKYOU)))
            .then(()=> dispatch(dataFetched()))
            .catch(error => dispatch(errorFetching(error, {action:changeSubscription})));
    }
}

export function getSubscriptionSummary(asAdmin, withBalance) {
    return function(dispatch, getState){
        const state = getState();
        const fetchUrl = '/dispatcher/WebStoreService.getSubscriptionSummary';
        const data = {
            farmKey: state.staticData.farmKey,
            seasonKey: state.staticData.seasonKey,
            memberKey: state.memberDetails.key,
            asAdmin: asAdmin,
            withBalance: withBalance
        };
        dispatch(fetchingData(FetchingTypes.GET_STATUS));
        return ajax(fetchUrl, data)
            .then(response => subscriptionSummaryParser({subscriptionSummary:response}, {staticData:state.staticData}))
            .then(summary => {
                dispatch(setSummary(summary))
            }).then(()=> dispatch(dataFetched()))
            .catch(error => {
                console.error(error);
                dispatch(errorFetching(error));
            })
    }
}

export function setShowAddressValidationMessage(showAddressValidationMessage) {
    return {
        type: SET_SHOW_ADDRESS_VALIDATION_MESSAGE,
        showAddressValidationMessage
    }
}
function isRedirectToFutureSeason(summary, farmUrl) {
    const hasSeasonUrl = window.location.pathname.split(farmUrl).filter(function (param) {
            return param !== "";
        }).length > 1;
    return !hasSeasonUrl && (summary || {}).status === SubscriptionStatuses.NO_SUBSCRIPTION && (summary.seasons || []).length > 1 && !summary.seasons[0].isActive;
}

export function deleteOrderAndUpdateVacation(vacationStart, vacationEnd) {
    return function(dispatch, getState) {
        const state = getState();
        const fetchUrl = '/dispatcher/WebStoreService.deleteOrder';
        const data = {
            farmKey: state.staticData.farmKey,
            memberKey: state.memberDetails.key,
            orderKey: state.summary.activeWebOrderKey
        };
        dispatch(fetchingData(FetchingTypes.UPDATE_VACATION));
        return ajax(fetchUrl, data)
            .then((json)=> {
                dispatch(setDirty(false));
                if(json.statusCode !== "OK") {
                    dispatch(errorFetching(json.message));
                } else {
                    dispatch(setWebOrder(undefined));
                    if(!state.summary.isSetOnPermanentHold) {
                        dispatch(updateVacations(vacationStart, vacationEnd));
                    } else {
                        dispatch(setSubscriptionOnPermanentHold());
                    }
                    dispatch(setCanSaveDeliveryHold(false));
                    dispatch(dataFetched())
                }
            })
            .catch(error => {
                dispatch(errorFetching(error));
            });
    }
}

export function sendCancelSubscriptionRequest() {
    return function(dispatch, getState) {
        const state = getState();
        const fetchUrl = '/dispatcher/WebStoreService.sendCancelSubscriptionRequest';
        const data = {
            farmKey: state.staticData.farmKey,
            memberKey: state.memberDetails.key,
            seasonKey: state.staticData.seasonKey,
        };
        dispatch(fetchingData(FetchingTypes.SUBMIT));
        return ajax(fetchUrl, data)
            .then((json) => {
                if(json.statusCode !== "OK") {
                    dispatch(errorFetching(json.message));
                } else {
                    dispatch(dataFetched());
                    alert("Your request was sent");
                }
            })
            .catch(error => {
                dispatch(errorFetching(error));
            })
    }
}

export function setSeasonRolloverOptOut(isOptOut) {
    return function(dispatch, getState) {
        const state = getState();
        const fetchUrl = '/dispatcher/WebStoreService.setSeasonRolloverOptOut';
        const data = {
            farmKey: state.staticData.farmKey,
            memberKey: state.memberDetails.key,
            seasonKey: state.staticData.seasonKey,
            isOptOut
        };
        dispatch(fetchingData(FetchingTypes.SUBMIT));
        return ajax(fetchUrl, data)
            .then((json) => {
                if(json.statusCode !== "OK") {
                    dispatch(errorFetching(json.message));
                } else {
                    dispatch(dataFetched());
                    alert("Your selection was saved");
                    window.location.reload(true);
                }
            })
            .catch(error => {
                dispatch(errorFetching(error));
            })
    }
}

export function unsubscribeFlag(flag) { {
    return function(dispatch, getState) {
        const state = getState();
        const fetchUrl = '/dispatcher/WebStoreService.setFlag';
        const data = {
            farmKey: state.staticData.farmKey,
            memberKey: state.memberDetails.key,
            flag
        };
        dispatch(fetchingData(FetchingTypes.SUBMIT));
        return ajax(fetchUrl, data)
            .then((json) => {
                if(json.statusCode !== "OK") {
                    dispatch(errorFetching(json.message));
                } else {
                    dispatch(dataFetched());
                    alert("Unsubscribed Successfully");
                    window.location.replace(window.location.origin + window.location.pathname)
                }
            })
            .catch(error => {
                dispatch(errorFetching(error));
            })
    }
}

}



// WEBPACK FOOTER //
// ./src/actions/account/accountActions.js